import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Link } from "@tanstack/react-router";
import { Eye, Pencil, Users } from "lucide-react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import {
  useDeleteOAuthProvider,
  useUpdateOAuthProvider,
} from "@/app/oauth/api/hooks";
import type { OAuthProviderItem } from "@/app/oauth/types";
import { DeleteDialog } from "@/components/custom/dialog/delete-dialog";
import { OAuthSheet } from "../credentials/oauth-sheet";

type OAuthActionsProps = {
  oauth: OAuthProviderItem;
};

export const OAuthActions = ({ oauth }: OAuthActionsProps) => {
  const { id, status, binding } = oauth;
  const { mutate: deleteOAuthProvider, isPending } = useDeleteOAuthProvider();
  const { mutate: updateOAuthProvider, isPending: isUpdating } =
    useUpdateOAuthProvider({
      oauthId: id,
      binding: binding.type,
    });

  const { canProject } = useProjectPermissions();
  const canUpdateProviders = canProject(PROJECT.authProviders.update);

  const is_static = binding.type === "static";
  const is_enabled = status === "active";
  // Static providers are config-file-locked; callers without update permission
  // are also read-only. Both open the sheet as a locked, view-only form.
  const isReadOnly = is_static || !canUpdateProviders;

  const handleDelete = async () => {
    await deleteOAuthProvider({
      oauthId: oauth.id,
      binding: oauth.binding.type,
    });
  };

  const handleToggleEnabled = async (checked: boolean) => {
    await updateOAuthProvider({
      id: oauth.id,
      status: checked ? "active" : "inactive",
    });
  };

  return (
    <div className="flex items-center space-x-1">
      {!is_static && (
        <CanProject permission={PROJECT.authProviders.update}>
          <TooltipProvider delay={200}>
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    aria-busy={isUpdating}
                    className="mr-3 inline-flex items-center"
                  />
                }
              >
                <Switch
                  checked={is_enabled}
                  loading={isUpdating}
                  onCheckedChange={handleToggleEnabled}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>{`${is_enabled ? "Disable" : "Enable"} OAuth provider`}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CanProject>
      )}
      <OAuthSheet
        canEdit={!isReadOnly}
        oauth={oauth}
        trigger={
          <Button
            aria-label={
              isReadOnly ? "View OAuth provider" : "Edit OAuth provider"
            }
            size="sm"
            variant="ghost"
          >
            {isReadOnly ? (
              <Eye className="size-4" />
            ) : (
              <Pencil className="size-4" />
            )}
          </Button>
        }
      />
      {/* The detail page leads with this provider's connected users, so gate it
          on user_connections_read — the connections endpoint 403s without it. */}
      <CanProject permission={PROJECT.userConnections.read}>
        <Link
          from="/orgs/$organizationId/projects/$projectId/auth/oauth/$oauthId"
          params={{ oauthId: id }}
          to="."
        >
          <Button aria-label="View connected users" size="sm" variant="ghost">
            <Users className="size-4" />
          </Button>
        </Link>
      </CanProject>
      {!is_static && (
        <CanProject permission={PROJECT.authProviders.delete}>
          <DeleteDialog
            description="Are you sure you want to delete this OAuth provider? This action cannot be undone."
            isLoading={isPending}
            onDelete={handleDelete}
            title="Delete OAuth Provider"
            tooltipText="Delete OAuth provider"
          />
        </CanProject>
      )}
    </div>
  );
};
